import * as React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ErrorPage } from '@web-config-app/core-react-ui';
import {
  useAuthoringEnvironments,
  useConfigApp,
} from '@web-config-app/core-react';
import { buildTenantPath, type EnvironmentKey } from '@web-config-app/core';
import { buildLegacyDomainRedirect } from '../legacy-url-redirect/legacy-url-utils';

/**
 * Returns the path segment immediately after `keyword` in a slash-delimited
 * path.  e.g. segmentAfter('/a/environments/foo/bar', 'environments') → 'foo'
 */
export const segmentAfter = (
  pathname: string,
  keyword: string,
): string | undefined => {
  const parts = pathname.split('/');
  const idx = parts.indexOf(keyword);
  return idx >= 0 && idx + 1 < parts.length ? parts[idx + 1] : undefined;
};

/**
 * Keys that still appear in bookmarked URLs but no longer identify an environment: every CA
 * `testX` once shared the generic `test` key and has since been given its own.
 *
 * Which `testX` an old link meant isn't recoverable from the URL, but it is from the tenant --
 * each has one CA test environment, and it's their default. Tenants where `test` is still
 * valid (au, cigna) never reach this path.
 */
const RETIRED_ENVIRONMENT_KEYS = ['test'];

/**
 * Validates route params extracted from the URL against the tenant's
 * configuration. Shows an error page when a param doesn't match any
 * configured value, except for a retired environment key, which is
 * redirected rather than rejected (see RETIRED_ENVIRONMENT_KEYS).
 *
 * Validates:
 * - tabId (top-level module) exists in the module tree
 * - environmentKey exists in the tenant's environment map
 *
 * Also handles legacy domain URLs: when a URL contains `/domains/` instead
 * of a valid tab ID, it redirects to the equivalent module-based URL by
 * looking up the entity in the module tree.
 *
 * We parse the pathname directly because this component renders at the
 * `/tenants/:tenantId` layout level — `useParams()` only has `tenantId`,
 * not the child route params like `tabId`.
 */
export const ValidateRouteParams = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const { tenantId } = useParams();
  const { entityConfigs, modules = [], tenantName } = useConfigApp();

  const {
    environmentMap,
    multiEnvironmentAuthoringEnabled,
    baseAuthoringEnvironment,
    defaultAuthoringEnvironment,
  } = useAuthoringEnvironments();

  const environmentKey = segmentAfter(location.pathname, 'environments');

  const tenantLanding = buildTenantPath(tenantId);

  // Extract tabId from path: /tenants/:tenantId/:tabId/...
  // Guard against empty strings from trailing slashes (e.g. /tenants/foo/)
  const pathParts = location.pathname.split('/');
  const tenantIndex = pathParts.indexOf('tenants');
  const tabIdSegment =
    tenantIndex >= 0 && tenantIndex + 2 < pathParts.length
      ? pathParts[tenantIndex + 2]
      : undefined;
  const tabId = tabIdSegment || undefined;

  if (tabId) {
    const currentTab = modules.find((tabModule) => tabModule.id === tabId);
    if (!currentTab) {
      // Check if this is a legacy domain URL and redirect if possible
      const legacyRedirect = buildLegacyDomainRedirect(
        location.pathname,
        location.search,
        tenantId,
        entityConfigs,
        modules,
      );

      if (legacyRedirect) {
        return <Navigate to={legacyRedirect} replace />;
      }

      return <ErrorPage homeUrl={tenantLanding} />;
    }
  }

  if (environmentKey) {
    const isValid =
      multiEnvironmentAuthoringEnabled && environmentMap
        ? environmentMap.has(environmentKey as EnvironmentKey)
        : environmentKey === baseAuthoringEnvironment.environmentKey;

    if (!isValid) {
      // Preview environments only. A retired key always named a lower environment, and
      // defaultAuthoringEnvironment falls back to the base -- production, in the production
      // deployment -- when a tenant's default can't be resolved. Sending an old test link
      // there silently would be worse than the error page.
      const redirectsToPreviewEnvironment =
        defaultAuthoringEnvironment.type !== 'base';

      if (
        RETIRED_ENVIRONMENT_KEYS.includes(environmentKey) &&
        redirectsToPreviewEnvironment
      ) {
        // Swap only the environment segment so the rest of the link survives. A mistyped key
        // isn't retired, so it falls through to the error page rather than being redirected.
        const pathname = location.pathname.replace(
          `/environments/${environmentKey}`,
          `/environments/${defaultAuthoringEnvironment.environmentKey}`,
        );
        return <Navigate to={{ pathname, search: location.search }} replace />;
      }

      return (
        <ErrorPage
          heading="ENVIRONMENT_NOT_FOUND_ON_TENANT"
          headingValues={{
            environmentKey,
            tenant: tenantName ?? tenantId ?? '',
          }}
          homeUrl={tenantLanding}
        />
      );
    }
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
